import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { functionService } from "../../lib/functions";
import * as Styled from "./styled";
import Sidemenu from "../../components/sidemenu";
import { Select, DatePicker, Spin } from "antd";
import dayjs from "dayjs";

const isAdmin = () => localStorage.getItem("adminAccess") <= "2";
const getMyName = () => localStorage.getItem("user_name");

const toYMD = (d) => dayjs(d).format("YYYY-MM-DD");
const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");
const todayYMD = () => toYMD(dayjs());
const addDays = (n) => toYMD(dayjs().add(n, "day"));
const startOfWeek = () => toYMD(dayjs().startOf("week").add(1, "day"));
const startOfMonth = () => toYMD(dayjs().startOf("month"));

// Steps back `stepsBack` working days (skipping Sat/Sun) from `fromDate`.
// stepsBack=1 -> the last working day ("Yesterday" logic: if today is
// Monday, this returns Friday). stepsBack=2 -> the working day before
// that (Monday -> Thursday, Tuesday -> Friday).
const previousWorkingDay = (fromDate, stepsBack) => {
    let d = dayjs(fromDate);
    let remaining = stepsBack;
    while (remaining > 0) {
        d = d.subtract(1, "day");
        if (d.day() !== 0 && d.day() !== 6) { // 0 = Sunday, 6 = Saturday
            remaining--;
        }
    }
    return toYMD(d);
};

const startOfLastWeek = () => toYMD(dayjs().startOf("week").add(1, "day").subtract(7, "day"));
const endOfLastWeek = () => toYMD(dayjs().startOf("week").add(1, "day").subtract(1, "day"));

const n = (val) => {
    if (val === "Not Required" || val === null || val === undefined) return 0;
    return Number(val) || 0;
};

const RECRUITER_PALETTE = [
    { bg: "#EDE9FE", fg: "#5B21B6" },
    { bg: "#FCE7F3", fg: "#9D174D" },
    { bg: "#D1FAE5", fg: "#065F46" },
    { bg: "#FEF3C7", fg: "#92400E" },
    { bg: "#DBEAFE", fg: "#1E40AF" },
    { bg: "#FFE4E6", fg: "#9F1239" },
];

const CLIENT_PALETTE = [
    { bg: "#FED7AA", fg: "#9A3412" },
    { bg: "#BBF7D0", fg: "#14532D" },
    { bg: "#BFDBFE", fg: "#1E3A8A" },
    { bg: "#DDD6FE", fg: "#4C1D95" },
    { bg: "#FECDD3", fg: "#9F1239" },
    { bg: "#FEF08A", fg: "#713F12" },
    { bg: "#BAE6FD", fg: "#0C4A6E" },
    { bg: "#F5D0FE", fg: "#701A75" },
    { bg: "#99F6E4", fg: "#134E4A" },
    { bg: "#FCA5A5", fg: "#7F1D1D" },
    { bg: "#C7D2FE", fg: "#312E81" },
    { bg: "#D9F99D", fg: "#365314" },
    { bg: "#FDBA74", fg: "#7C2D12" },
    { bg: "#A5F3FC", fg: "#164E63" },
    { bg: "#E9D5FF", fg: "#581C87" },
    { bg: "#FDE68A", fg: "#92400E" },
];

const hashStr = (str) => {
    let h = 5381;
    for (let i = 0; i < (str || "").length; i++) {
        h = ((h << 5) + h) ^ str.charCodeAt(i);
        h = h & h;
    }
    return Math.abs(h);
};

const clientColor = (name) =>
    CLIENT_PALETTE[hashStr(name || "") % CLIENT_PALETTE.length];

const COL_STYLE = {
    approval_pending: { bg: "#FEF3C7", fg: "#92400E" },
    approved_submission: { bg: "#D1FAE5", fg: "#065F46" },
    rejected_submission: { bg: "#FEE2E2", fg: "#991B1B" },
    assessed: { bg: "#EDE9FE", fg: "#5B21B6" },
    score_above_50: { bg: "#E0E7FF", fg: "#3730A3" },
    score_above_65: { bg: "#eeeeffff", fg: "#3d3aa3ff" },
    final_submission: { bg: "#D1FAE5", fg: "#065F46" },
};

const ColorNum = ({ v, colKey }) => {
    const val = n(v);
    const cfg = COL_STYLE[colKey];
    if (!cfg || val === 0) return <Styled.NumSpan zero>{0}</Styled.NumSpan>;
    return <Styled.ColorPill bg={cfg.bg} fg={cfg.fg}>{val}</Styled.ColorPill>;
};

const PlainNum = ({ v }) => {
    const val = n(v);
    return <Styled.NumSpan zero={val === 0}>{val}</Styled.NumSpan>;
};

const SentNum = ({ v }) => {
    const val = n(v);
    return <Styled.NumSpan zero={val === 0}>{val}</Styled.NumSpan>;
};

const AssessmentBadge = ({ enabled }) => (
    enabled
        ? <Styled.AssessYesBadge>Yes</Styled.AssessYesBadge>
        : <Styled.AssessNoBadge>No</Styled.AssessNoBadge>
);

const RoleCell = ({ title }) => {
    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const spanRef = useRef(null);

    const show = () => {
        const rect = spanRef.current.getBoundingClientRect();
        let top = rect.bottom + 6;
        let left = rect.left;
        if (top + 40 > window.innerHeight - 12) top = rect.top - 34;
        if (left + 400 > window.innerWidth - 12) left = window.innerWidth - 412;
        if (left < 8) left = 8;
        setPos({ top, left });
        setVisible(true);
    };

    return (
        <Styled.RoleCellWrapper>
            <span
                ref={spanRef}
                className="role-text"
                onMouseEnter={show}
                onMouseLeave={() => setVisible(false)}
            >
                {title}
            </span>
            {ReactDOM.createPortal(
                <Styled.RoleTooltip visible={visible} style={{ top: pos.top, left: pos.left }}>
                    {title}
                </Styled.RoleTooltip>,
                document.body
            )}
        </Styled.RoleCellWrapper>
    );
};

const calcFinalSubmission = (row) => {
    if (!row.assessment_enabled) return n(row.approved_submission);
    return Math.min(n(row.approved_submission), n(row.score_above_65));
};

// ================================
// TEMP: Debrief dashboard mock data
// Replace with backend response later
// ================================
const DEBRIEF_MOCK_DATA = [
    {
        recruiter_name: "Muskan Rastogi",
        interviews_scheduled: 1,
        interviews_done: 1,
        debrief_completed: 1,
        interviews_not_done: 0,
        rescheduled_interview: 0,
        client_led: 0,
        candidate_led: 0,
    },
    {
        recruiter_name: "Neelesh Dube",
        interviews_scheduled: 3,
        interviews_done: 3,
        debrief_completed: 3,
        interviews_not_done: 0,
        rescheduled_interview: 0,
        client_led: 0,
        candidate_led: 0,
    },
    {
        recruiter_name: "Sony Raj",
        interviews_scheduled: 13,
        interviews_done: 11,
        debrief_completed: 9,
        interviews_not_done: 2,
        rescheduled_interview: 1, // extra column requested
        client_led: 0,
        candidate_led: 2,
    },
    {
        recruiter_name: "Muskan Sharma",
        interviews_scheduled: 1,
        interviews_done: 1,
        debrief_completed: 1,
        interviews_not_done: 0,
        rescheduled_interview: 0,
        client_led: 0,
        candidate_led: 0,
    },
    {
        recruiter_name: "Nandini Gupta",
        interviews_scheduled: 3,
        interviews_done: 2,
        debrief_completed: 1,
        interviews_not_done: 1,
        rescheduled_interview: 1,
        client_led: 0,
        candidate_led: 1,
    },
    {
        recruiter_name: "Vaishnavi Pathak",
        interviews_scheduled: 5,
        interviews_done: 4,
        debrief_completed: 4,
        interviews_not_done: 1,
        rescheduled_interview: 0,
        client_led: 1,
        candidate_led: 0,
    },
];

const safePct = (num, den) => {
    if (!den) return 0;
    return Math.round((n(num) / n(den)) * 100);
};



const PerformanceReport = () => {

    const admin = isAdmin();
    const myName = getMyName();

    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(false);
    const savedMode = localStorage.getItem("perf_range_mode") || "yesterday";
    const savedFrom = localStorage.getItem("perf_from_date") || previousWorkingDay(todayYMD(), 1);
    const savedTo = localStorage.getItem("perf_to_date") || previousWorkingDay(todayYMD(), 1);

    const datesFromMode = (mode) => {
        const today = todayYMD();
        const yesterday = previousWorkingDay(today, 1);
        if (mode === "today") return { from: today, to: today };
        if (mode === "yesterday") return { from: yesterday, to: yesterday };
        if (mode === "dayBefore") {
            const priorDay = previousWorkingDay(today, 2);
            return { from: priorDay, to: priorDay };
        }
        if (mode === "week") return { from: startOfWeek(), to: today };
        if (mode === "lastWeek") return { from: startOfLastWeek(), to: endOfLastWeek() };
        if (mode === "month") return { from: startOfMonth(), to: today };
        return null;
    };

    const computed = datesFromMode(savedMode);

    const [rangeMode, setRangeMode] = useState(savedMode);
    const [fromDate, setFromDate] = useState(computed ? computed.from : savedFrom);
    const [toDate, setToDate] = useState(computed ? computed.to : savedTo);
    const [filterRecruiter, setFilterRecruiter] = useState(null);
    const [filterClient, setFilterClient] = useState(null);
    const [filterEndClient, setFilterEndClient] = useState(null);
    const [filterSubAccount, setFilterSubAccount] = useState(null);
    const [openRecruiters, setOpenRecruiters] = useState(() => {
        if (!isAdmin()) return { [getMyName()]: true };
        return {};
    });



    const [dashboardType, setDashboardType] = useState("performance"); //added
    const [debriefData, setDebriefData] = useState([]);
    // const [debriefGrandTotal, setDebriefGrandTotal] = useState({});

    const [filterAssessment, setFilterAssessment] = useState(null);
    const assessmentOptions = [
        { label: "Yes", value: true },
        { label: "No", value: false }
    ];


    const fetchReport = async (from, to) => {
        setLoading(true);
        try {
            const res = await functionService.get(`demand/performance-report?from=${from}&to=${to}`);
            setRawData(res?.response?.data?.data ?? []);
        } catch (err) {
            console.error("PerformanceReport fetch error:", err);
            setRawData([]);
        } finally {
            setLoading(false);
        }
    };

    const getDebriefDashboardData = async () => {
        try {
            setLoading(true);

            const result =
                await functionService.getInterviewDebriefReport(
                    fromDate,
                    toDate
                );

            if (result.status) {
                setDebriefData(
                    result.response.data.data.rows || []
                );
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        localStorage.setItem("perf_range_mode", rangeMode);
        localStorage.setItem("perf_from_date", fromDate);
        localStorage.setItem("perf_to_date", toDate);
    }, [rangeMode, fromDate, toDate]);

    useEffect(() => {

        if (dashboardType === "performance") {
            fetchReport(fromDate, toDate);
        }

        if (dashboardType === "debrief") {
            getDebriefDashboardData();
        }

    }, [dashboardType, rangeMode, fromDate, toDate]);

    const filteredData = useMemo(() => {
        let d = rawData;
        if (!admin) d = d.filter((r) => r.recruiter_name === myName);
        if (filterRecruiter) d = d.filter((r) => r.recruiter_name === filterRecruiter);
        if (filterClient) d = d.filter((r) => r.client === filterClient);
        if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
        if (filterSubAccount) d = d.filter((r) => r.sub_account === filterSubAccount);
        if (filterAssessment !== null) {
            d = d.filter(
                (r) => Boolean(r.assessment_enabled) === filterAssessment
            );
        }
        return d;
    }, [rawData, admin, myName, filterRecruiter, filterClient, filterEndClient, filterSubAccount, filterAssessment]);

    // Each options list is filtered by the OTHER active filters (not its own)
    const recruiterOptions = useMemo(() => {
        let d = rawData;
        if (!admin) d = d.filter((r) => r.recruiter_name === myName);
        if (filterClient) d = d.filter((r) => r.client === filterClient);
        if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
        if (filterSubAccount) d = d.filter((r) => r.sub_account === filterSubAccount);
        if (filterAssessment !== null) {
            d = d.filter(
                (r) => Boolean(r.assessment_enabled) === filterAssessment
            );
        }
        return [...new Set(d.map((r) => r.recruiter_name))].filter(Boolean).sort();
    }, [rawData, admin, myName, filterClient, filterEndClient, filterSubAccount, filterAssessment]);

    const clientOptions = useMemo(() => {
        let d = rawData;
        if (!admin) d = d.filter((r) => r.recruiter_name === myName);
        if (filterRecruiter) d = d.filter((r) => r.recruiter_name === filterRecruiter);
        if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
        if (filterSubAccount) d = d.filter((r) => r.sub_account === filterSubAccount);
        if (filterAssessment !== null) {
            d = d.filter(
                (r) => Boolean(r.assessment_enabled) === filterAssessment
            );
        }
        return [...new Set(d.map((r) => r.client))].filter(Boolean).sort();
    }, [rawData, admin, myName, filterRecruiter, filterEndClient, filterSubAccount, filterAssessment]);

    const endClientOptions = useMemo(() => {
        let d = rawData;
        if (!admin) d = d.filter((r) => r.recruiter_name === myName);
        if (filterRecruiter) d = d.filter((r) => r.recruiter_name === filterRecruiter);
        if (filterClient) d = d.filter((r) => r.client === filterClient);
        if (filterSubAccount) d = d.filter((r) => r.sub_account === filterSubAccount);
        if (filterAssessment !== null) {
            d = d.filter(
                (r) => Boolean(r.assessment_enabled) === filterAssessment
            );
        }
        return [...new Set(d.map((r) => r.end_client))].filter(Boolean).sort();
    }, [rawData, admin, myName, filterRecruiter, filterClient, filterSubAccount, filterAssessment]);


    const subAccountOptions = useMemo(() => {
        let d = rawData;
        if (!admin) d = d.filter((r) => r.recruiter_name === myName);
        if (filterRecruiter) d = d.filter((r) => r.recruiter_name === filterRecruiter);
        if (filterClient) d = d.filter((r) => r.client === filterClient);
        if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
        if (filterAssessment !== null) {
            d = d.filter(
                (r) => Boolean(r.assessment_enabled) === filterAssessment
            );
        }
        return [...new Set(d.map((r) => r.sub_account))].filter(Boolean).sort();
    }, [rawData, admin, myName, filterRecruiter, filterClient, filterEndClient, filterAssessment]);

    const grouped = useMemo(() => {
        const map = {};
        filteredData.forEach((row) => {
            if (!map[row.recruiter_name]) map[row.recruiter_name] = [];
            map[row.recruiter_name].push(row);
        });
        return map;
    }, [filteredData]);

    const recruiterNames = useMemo(() => Object.keys(grouped).sort(), [grouped]);

    const recruiterColorMap = useMemo(() => {
        const allNames = [...new Set(rawData.map((r) => r.recruiter_name))].filter(Boolean).sort();
        const map = {};
        allNames.forEach((name, i) => { map[name] = RECRUITER_PALETTE[i % RECRUITER_PALETTE.length]; });
        return map;
    }, [rawData]);

    const grand = useMemo(() => {
        const sum = (key) => filteredData.reduce((acc, row) => acc + n(row[key]), 0);
        return {
            profiles_submitted: sum("profiles_submitted"),
            approval_pending: sum("approval_pending"),
            approved_submission: sum("approved_submission"),
            rejected_submission: sum("rejected_submission"),
            assessed: sum("assessed"),
            score_above_50: sum("score_above_50"),
            score_above_65: sum("score_above_65"),
            final_submission: filteredData.reduce((acc, row) => acc + calcFinalSubmission(row), 0),
            sent_to_client: sum("sent_to_client"),
        };
    }, [filteredData]);

    const summaryCards = [
        { label: "Profiles Submitted", val: grand.profiles_submitted, color: "#4F46E5", light: "#EEF2FF" },
        { label: "Approved", val: grand.approved_submission, color: "#10B981", light: "#D1FAE5" },
        { label: "Rejected", val: grand.rejected_submission, color: "#EF4444", light: "#FEE2E2" },
        { label: "Assessments Done", val: grand.assessed, color: "#8B5CF6", light: "#EDE9FE" },
        { label: "Final Submission", val: grand.final_submission, color: "#059669", light: "#D1FAE5" },
        { label: "Shared with Client", val: grand.sent_to_client, color: "#F59E0B", light: "#FEF3C7" },
    ];

    const applyRange = (mode) => {
        setRangeMode(mode);
        const today = todayYMD();
        const yesterday = previousWorkingDay(today, 1);
        if (mode === "today") { setFromDate(today); setToDate(today); }
        if (mode === "yesterday") { setFromDate(yesterday); setToDate(yesterday); }
        if (mode === "dayBefore") {
            const priorDay = previousWorkingDay(today, 2);
            setFromDate(priorDay); setToDate(priorDay);
        }
        if (mode === "week") { setFromDate(startOfWeek()); setToDate(today); }
        if (mode === "lastWeek") { setFromDate(startOfLastWeek()); setToDate(endOfLastWeek()); }
        if (mode === "month") { setFromDate(startOfMonth()); setToDate(today); }
    };

    const toggleRecruiter = (name) =>
        setOpenRecruiters((prev) => ({ ...prev, [name]: !prev[name] }));

    const rowTotal = (rows, key) => rows.reduce((acc, row) => acc + n(row[key]), 0);
    const rowFinalTotal = (rows) => rows.reduce((acc, row) => acc + calcFinalSubmission(row), 0);

    const dateLabel = fromDate === toDate ? fmt(fromDate) : `${fmt(fromDate)} – ${fmt(toDate)}`;
    const isCustom = rangeMode === "custom";
    const MIN_DATE = dayjs("2026-04-22").startOf("day");
    const disableFutureDates = (current) =>
        current && (current > dayjs().endOf("day") || current < MIN_DATE);

    // ================================
    // TEMP: Debrief dashboard frontend-only derived data
    // Replace with backend integration later
    // ================================
    const debriefFilteredData = useMemo(() => {
        let d = debriefData; // IMPORTANT: use backend debrief dataset here

        if (!admin) d = d.filter((r) => r.recruiter_name === myName);

        if (filterRecruiter) d = d.filter((r) => r.recruiter_name === filterRecruiter);
        if (filterClient) d = d.filter((r) => r.company_name === filterClient);
        if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
        if (filterSubAccount) d = d.filter((r) => r.sub_account === filterSubAccount);

        return d;
    }, [
        debriefData,
        admin,
        myName,
        filterRecruiter,
        filterClient,
        filterEndClient,
        filterSubAccount
    ]);

    // Debrief recruiter options (filtered by other active filters)
    const debriefRecruiterOptions = useMemo(() => {
        let d = debriefData;
        if (!admin) d = d.filter((r) => r.recruiter_name === myName);
        if (filterClient) d = d.filter((r) => r.company_name === filterClient);
        if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
        if (filterSubAccount) d = d.filter((r) => r.sub_account === filterSubAccount);

        return [...new Set(d.map((r) => r.recruiter_name))]
            .filter(Boolean)
            .sort();
    }, [debriefData, admin, myName, filterClient, filterEndClient, filterSubAccount]);

    // Debrief client options (filtered by other active filters)
    const debriefClientOptions = useMemo(() => {
        let d = debriefData;
        if (!admin) d = d.filter((r) => r.recruiter_name === myName);
        if (filterRecruiter) d = d.filter((r) => r.recruiter_name === filterRecruiter);
        if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
        if (filterSubAccount) d = d.filter((r) => r.sub_account === filterSubAccount);

        return [...new Set(d.map((r) => r.company_name))]
            .filter(Boolean)
            .sort();
    }, [debriefData, admin, myName, filterRecruiter, filterEndClient, filterSubAccount]);

    // Debrief end-client options (filtered by other active filters)
    const debriefEndClientOptions = useMemo(() => {
        let d = debriefData;
        if (!admin) d = d.filter((r) => r.recruiter_name === myName);
        if (filterRecruiter) d = d.filter((r) => r.recruiter_name === filterRecruiter);
        if (filterClient) d = d.filter((r) => r.company_name === filterClient);
        if (filterSubAccount) d = d.filter((r) => r.sub_account === filterSubAccount);

        return [...new Set(d.map((r) => r.end_client))]
            .filter(Boolean)
            .sort();
    }, [debriefData, admin, myName, filterRecruiter, filterClient, filterSubAccount]);

    // Debrief sub-account options (filtered by other active filters)
    const debriefSubAccountOptions = useMemo(() => {
        let d = debriefData;
        if (!admin) d = d.filter((r) => r.recruiter_name === myName);
        if (filterRecruiter) d = d.filter((r) => r.recruiter_name === filterRecruiter);
        if (filterClient) d = d.filter((r) => r.company_name === filterClient);
        if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);

        return [...new Set(d.map((r) => r.sub_account))]
            .filter(Boolean)
            .sort();
    }, [debriefData, admin, myName, filterRecruiter, filterClient, filterEndClient]);
    const debriefGrand = useMemo(() => {
        const sum = (key) => debriefFilteredData.reduce((acc, row) => acc + n(row[key]), 0);

        const interviews_scheduled = sum("interviews_scheduled");
        const interviews_done = sum("interviews_done");
        const debrief_completed = sum("debrief_completed");
        const interviews_not_done = sum("interviews_not_done");
        const rescheduled_interview = sum("rescheduled_interview");
        const client_led = sum("client_led");
        const candidate_led = sum("candidate_led");

        return {
            interviews_scheduled,
            interviews_done,
            interviews_done_pct: safePct(interviews_done, interviews_scheduled),
            debrief_completed,
            debrief_completed_pct: safePct(debrief_completed, interviews_done || interviews_scheduled),
            interviews_not_done,
            rescheduled_interview,
            client_led,
            candidate_led,
        };
    }, [debriefFilteredData]);

    // TEMP: Debrief grouped data for expandable recruiter rows
    const debriefGrouped = useMemo(() => {
        const map = {};
        debriefFilteredData.forEach((row) => {
            if (!map[row.recruiter_name]) map[row.recruiter_name] = [];
            map[row.recruiter_name].push(row);
        });
        return map;
    }, [debriefFilteredData]);

    const debriefRecruiterNames = useMemo(
        () => Object.keys(debriefGrouped).sort(),
        [debriefGrouped]
    );

    const debriefSummaryCards = [
        { label: "Interviews Scheduled", val: debriefGrand.interviews_scheduled, color: "#4F46E5", light: "#EEF2FF" },
        { label: "Interviews Done", val: debriefGrand.interviews_done, color: "#10B981", light: "#D1FAE5" },
        { label: "Debrief Completed", val: debriefGrand.debrief_completed, color: "#8B5CF6", light: "#EDE9FE" },
        { label: "Interviews Not Done", val: debriefGrand.interviews_not_done, color: "#EF4444", light: "#FEE2E2" },
        // { label: "Rescheduled Interview", val: debriefGrand.rescheduled_interview, color: "#F59E0B", light: "#FEF3C7" },
        { label: "Client Led", val: debriefGrand.client_led, color: "#0891B2", light: "#E0F2FE" },
        { label: "Candidate Led", val: debriefGrand.candidate_led, color: "#9333EA", light: "#F3E8FF" },
    ];

    return (
        <Styled.MainDiv>
            <Sidemenu />
            <Styled.ContentWrapDiv>

                <Styled.TopContainer>
                    <div className="left">
                        <Styled.DashboardSwitch>
                            <Styled.SwitchTab
                                active={dashboardType === "performance"}
                                onClick={() => setDashboardType("performance")}
                            >
                                Performance Report
                            </Styled.SwitchTab>

                            <Styled.SwitchTab
                                active={dashboardType === "debrief"}
                                onClick={() => setDashboardType("debrief")}
                            >
                                Debrief Dashboard
                            </Styled.SwitchTab>
                        </Styled.DashboardSwitch>

                        <div className="date-label">
                            Showing: {dateLabel}
                        </div>
                    </div>

                    <div className="right">
                        <Styled.RangeTabsWrapper>
                            {[
                                { key: "today", label: "Today" },
                                { key: "yesterday", label: `Last ${dayjs(previousWorkingDay(todayYMD(), 1)).format("dddd")}` },
                                { key: "dayBefore", label: `Last ${dayjs(previousWorkingDay(todayYMD(), 2)).format("dddd")}` },
                                { key: "week", label: "This Week" },
                                { key: "lastWeek", label: "Last Week" },
                                { key: "month", label: "This Month" },
                                { key: "custom", label: "Custom" },
                            ].map((t) => (
                                <Styled.RangeTab
                                    key={t.key}
                                    active={rangeMode === t.key}
                                    onClick={() => applyRange(t.key)}
                                >
                                    {t.label}
                                </Styled.RangeTab>
                            ))}
                        </Styled.RangeTabsWrapper>
                    </div>
                </Styled.TopContainer>

                {/* <Styled.TopContainer>
                    <div className="left">
                        <div className="page-title">Performance Report</div>
                        <div className="date-label">Showing: {dateLabel}</div>
                    </div>
                    <div className="right">
                        <Styled.RangeTabsWrapper>
                            {[
                                { key: "today",     label: "Today" },
                                { key: "yesterday", label: "Yesterday" },
                                { key: "3days",     label: "Last 3 Days" },
                                { key: "week",      label: "This Week" },
                                { key: "month",     label: "This Month" },
                                { key: "custom",    label: "Custom" },
                            ].map((t) => (
                                <Styled.RangeTab key={t.key} active={rangeMode === t.key} onClick={() => applyRange(t.key)}>
                                    {t.label}
                                </Styled.RangeTab>
                            ))}
                        </Styled.RangeTabsWrapper>
                    </div>
                </Styled.TopContainer> */}

                {
                    dashboardType === "performance" && (
                        <>
                            <Styled.FilterRow>
                                {admin && (
                                    <Styled.StyledSelectWrap>
                                        <Select showSearch placeholder="All recruiters" value={filterRecruiter}
                                            onChange={setFilterRecruiter} allowClear style={{ width: 190 }}>
                                            {recruiterOptions.map((r) => {
                                                const palette = recruiterColorMap[r] || RECRUITER_PALETTE[0];
                                                return (
                                                    <Select.Option key={r} value={r}>
                                                        <Styled.FilterRecruiterChip bg={palette.bg} fg={palette.fg}>{r}</Styled.FilterRecruiterChip>
                                                    </Select.Option>
                                                );
                                            })}
                                        </Select>
                                    </Styled.StyledSelectWrap>
                                )}

                                <Styled.StyledSelectWrap>
                                    <Select showSearch placeholder="All Accounts" value={filterClient}
                                        onChange={setFilterClient} allowClear style={{ width: 170 }}>
                                        {clientOptions.map((c) => {
                                            const cc = clientColor(c);
                                            return (
                                                <Select.Option key={c} value={c}>
                                                    <Styled.FilterClientChip bg={cc.bg} fg={cc.fg}>{c}</Styled.FilterClientChip>
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </Styled.StyledSelectWrap>

                                <Styled.StyledSelectWrap>
                                    <Select showSearch placeholder="All sub accounts" value={filterSubAccount}
                                        onChange={setFilterSubAccount} allowClear style={{ width: 170 }}>
                                        {subAccountOptions.map((s) => {
                                            const cc = clientColor(s);
                                            return (
                                                <Select.Option key={s} value={s}>
                                                    <Styled.FilterClientChip bg={cc.bg} fg={cc.fg}>{s}</Styled.FilterClientChip>
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </Styled.StyledSelectWrap>

                                <Styled.StyledSelectWrap>
                                    <Select showSearch placeholder="All end clients" value={filterEndClient}
                                        onChange={setFilterEndClient} allowClear style={{ width: 170 }}>
                                        {endClientOptions.map((c) => {
                                            const cc = clientColor(c);
                                            return (
                                                <Select.Option key={c} value={c}>
                                                    <Styled.FilterClientChip bg={cc.bg} fg={cc.fg}>{c}</Styled.FilterClientChip>
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </Styled.StyledSelectWrap>
                                <Styled.StyledSelectWrap>

                                    <Select
                                        showSearch
                                        placeholder="Assessment"
                                        value={filterAssessment}
                                        onChange={(value) => setFilterAssessment(value === undefined ? null : value)}
                                        allowClear
                                        style={{ width: 170 }}
                                    >
                                        {assessmentOptions.map((option) => (
                                            <Select.Option
                                                key={String(option.value)}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </Select.Option>
                                        ))}
                                    </Select>

                                </Styled.StyledSelectWrap>


                                {isCustom && (
                                    <>
                                        <Styled.StyledDatePickerWrap>
                                            <DatePicker placeholder="From date" value={fromDate ? dayjs(fromDate) : null}
                                                format="DD MMM YYYY" allowClear={false} disabledDate={disableFutureDates}
                                                onChange={(d) => { if (d) setFromDate(d.format("YYYY-MM-DD")); }}
                                                style={{ width: 170 }} />
                                        </Styled.StyledDatePickerWrap>

                                        <span className="date-sep">→</span>

                                        <Styled.StyledDatePickerWrap>
                                            <DatePicker placeholder="To date" value={toDate ? dayjs(toDate) : null}
                                                format="DD MMM YYYY" allowClear={false} disabledDate={disableFutureDates}
                                                onChange={(d) => { if (d) setToDate(d.format("YYYY-MM-DD")); }}
                                                style={{ width: 170 }} />
                                        </Styled.StyledDatePickerWrap>
                                    </>
                                )}
                            </Styled.FilterRow>

                            <Styled.SummaryGrid>
                                {summaryCards.map((card, idx) => (
                                    <Styled.SummaryCard key={card.label} color={card.color} light={card.light} idx={idx}>
                                        <Styled.SummaryValue color={card.color}>{card.val}</Styled.SummaryValue>
                                        <Styled.SummaryLabel>{card.label}</Styled.SummaryLabel>
                                    </Styled.SummaryCard>
                                ))}
                            </Styled.SummaryGrid>

                            <Styled.TableScrollArea>
                                {loading ? (
                                    <Styled.SpinWrap><Spin size="large" /></Styled.SpinWrap>
                                ) : recruiterNames.length === 0 ? (
                                    <Styled.EmptyWrap>
                                        <div className="icon">📋</div>
                                        <div>No data found for the selected range</div>
                                    </Styled.EmptyWrap>
                                ) : (
                                    <Styled.MainTable>
                                        <thead>
                                            <tr>
                                                <Styled.THMeta left rowSpan={2} style={{ width: "16%" }}>Recruiter / Role</Styled.THMeta>
                                                <Styled.THMeta rowSpan={2} style={{ width: "7%" }}>Account<br />Name</Styled.THMeta>
                                                <Styled.THMeta rowSpan={2} style={{ width: "6%" }}>Assessment</Styled.THMeta>
                                                <Styled.THMeta rowSpan={2} style={{ width: "6%" }}><>Profiles<br />Submitted</></Styled.THMeta>
                                                <Styled.THGroup colSpan={3} groupColor="#F59E0B">Review Status</Styled.THGroup>
                                                <Styled.THGroup colSpan={3} groupColor="#8B5CF6">Assessment Status</Styled.THGroup>
                                                <Styled.THMeta rowSpan={2} style={{ width: "7%" }}><>Final<br />Submission</></Styled.THMeta>
                                                <Styled.THMeta rowSpan={2} style={{ width: "7%" }}><>Shared<br />With Client</></Styled.THMeta>
                                            </tr>
                                            <tr>
                                                <Styled.THSub bg="#FEF3C7" color="#92400E">Pending</Styled.THSub>
                                                <Styled.THSub bg="#D1FAE5" color="#065F46">Approved</Styled.THSub>
                                                <Styled.THSub bg="#FEE2E2" color="#991B1B">Rejected</Styled.THSub>
                                                <Styled.THSub bg="#EDE9FE" color="#5B21B6">Assessed</Styled.THSub>
                                                <Styled.THSub bg="#E0E7FF" color="#3730A3" style={{ whiteSpace: "nowrap" }}>Score ≥ 50%</Styled.THSub>
                                                <Styled.THSub bg="#eef0ffff" color="#312E81" style={{ whiteSpace: "nowrap" }}>Score ≥ 65%</Styled.THSub>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {recruiterNames.map((recruiter) => {
                                                const rows = grouped[recruiter];
                                                const isOpen = !!openRecruiters[recruiter];
                                                const palette = recruiterColorMap[recruiter] || RECRUITER_PALETTE[0];

                                                const rSub = rowTotal(rows, "profiles_submitted");
                                                const rPend = rowTotal(rows, "approval_pending");
                                                const rApp = rowTotal(rows, "approved_submission");
                                                const rRej = rowTotal(rows, "rejected_submission");
                                                const rAss = rowTotal(rows, "assessed");
                                                const r50 = rowTotal(rows, "score_above_50");
                                                const r65 = rowTotal(rows, "score_above_65");
                                                const rFinal = rowFinalTotal(rows);
                                                const rSent = rowTotal(rows, "sent_to_client");

                                                return (
                                                    <React.Fragment key={recruiter}>
                                                        <Styled.RecruiterRow onClick={() => toggleRecruiter(recruiter)}>
                                                            <td className="left">
                                                                <Styled.RecruiterNameCell>
                                                                    <Styled.Chevron open={isOpen}>▶</Styled.Chevron>
                                                                    <Styled.RecruiterChip bg={palette.bg} fg={palette.fg}>{recruiter}</Styled.RecruiterChip>
                                                                </Styled.RecruiterNameCell>
                                                            </td>
                                                            <td></td>
                                                            <td></td>
                                                            <td><PlainNum v={rSub} /></td>
                                                            <td><ColorNum v={rPend} colKey="approval_pending" /></td>
                                                            <td><ColorNum v={rApp} colKey="approved_submission" /></td>
                                                            <td><ColorNum v={rRej} colKey="rejected_submission" /></td>
                                                            <td><ColorNum v={rAss} colKey="assessed" /></td>
                                                            <td><ColorNum v={r50} colKey="score_above_50" /></td>
                                                            <td><ColorNum v={r65} colKey="score_above_65" /></td>
                                                            <td><ColorNum v={rFinal} colKey="final_submission" /></td>
                                                            <td><SentNum v={rSent} /></td>
                                                        </Styled.RecruiterRow>

                                                        {isOpen && rows.map((row, idx) => {
                                                            const cc = clientColor(row.client);
                                                            const finalVal = calcFinalSubmission(row);
                                                            return (
                                                                <Styled.DemandRow key={idx}>
                                                                    <RoleCell title={row.role_title} />
                                                                    <td>
                                                                        <Styled.ClientChip style={{ background: cc.bg, color: cc.fg }}>
                                                                            {row.client}
                                                                        </Styled.ClientChip>
                                                                    </td>
                                                                    <td><AssessmentBadge enabled={row.assessment_enabled} /></td>
                                                                    <td><PlainNum v={row.profiles_submitted} /></td>
                                                                    <td><ColorNum v={row.approval_pending} colKey="approval_pending" /></td>
                                                                    <td><ColorNum v={row.approved_submission} colKey="approved_submission" /></td>
                                                                    <td><ColorNum v={row.rejected_submission} colKey="rejected_submission" /></td>
                                                                    <td><ColorNum v={row.assessed} colKey="assessed" /></td>
                                                                    <td><ColorNum v={row.score_above_50} colKey="score_above_50" /></td>
                                                                    <td><ColorNum v={row.score_above_65} colKey="score_above_65" /></td>
                                                                    <td><ColorNum v={finalVal} colKey="final_submission" /></td>
                                                                    <td><SentNum v={row.sent_to_client} /></td>
                                                                </Styled.DemandRow>
                                                            );
                                                        })}

                                                        {admin && isOpen && (
                                                            <Styled.SubtotalRow>
                                                                <td className="subtotal-name" colSpan={3}>{recruiter} — Total</td>
                                                                <td>{rSub}</td>
                                                                <td>{rPend}</td>
                                                                <td>{rApp}</td>
                                                                <td>{rRej}</td>
                                                                <td>{rAss}</td>
                                                                <td>{r50}</td>
                                                                <td>{r65}</td>
                                                                <td>{rFinal}</td>
                                                                <td>{rSent}</td>
                                                            </Styled.SubtotalRow>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}

                                            <Styled.GrandTotalRow>
                                                <td className="left label" colSpan={3}>Grand Total</td>
                                                <td>{grand.profiles_submitted}</td>
                                                <td>{grand.approval_pending}</td>
                                                <td>{grand.approved_submission}</td>
                                                <td>{grand.rejected_submission}</td>
                                                <td>{grand.assessed}</td>
                                                <td>{grand.score_above_50}</td>
                                                <td>{grand.score_above_65}</td>
                                                <td>{grand.final_submission}</td>
                                                <td>{grand.sent_to_client}</td>
                                            </Styled.GrandTotalRow>
                                        </tbody>
                                    </Styled.MainTable>
                                )}
                            </Styled.TableScrollArea>
                        </>
                    )
                }

                {
                    dashboardType === "debrief" && (
                        <>
                            {/* TEMP: Reusing existing shared filters for visual/UX consistency.
               Debrief backend wiring is intentionally disabled for now. */}
                            <Styled.FilterRow>
                                {admin && (
                                    <Styled.StyledSelectWrap>
                                        <Select
                                            showSearch
                                            placeholder="All recruiters"
                                            value={filterRecruiter}
                                            onChange={setFilterRecruiter}
                                            allowClear
                                            style={{ width: 190 }}
                                        >
                                            {recruiterOptions.map((r) => {
                                                const palette = recruiterColorMap[r] || RECRUITER_PALETTE[0];
                                                return (
                                                    <Select.Option key={r} value={r}>
                                                        <Styled.FilterRecruiterChip bg={palette.bg} fg={palette.fg}>
                                                            {r}
                                                        </Styled.FilterRecruiterChip>
                                                    </Select.Option>
                                                );
                                            })}
                                        </Select>
                                    </Styled.StyledSelectWrap>
                                )}

                                {/* Intentionally kept for shared-page consistency */}
                                <Styled.StyledSelectWrap>
                                    <Select
                                        showSearch
                                        placeholder="All Accounts"
                                        value={filterClient}
                                        onChange={setFilterClient}
                                        allowClear
                                        style={{ width: 170 }}
                                    >
                                        {clientOptions.map((c) => {
                                            const cc = clientColor(c);
                                            return (
                                                <Select.Option key={c} value={c}>
                                                    <Styled.FilterClientChip bg={cc.bg} fg={cc.fg}>{c}</Styled.FilterClientChip>
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </Styled.StyledSelectWrap>

                                <Styled.StyledSelectWrap>
                                    <Select
                                        showSearch
                                        placeholder="All sub accounts"
                                        value={filterSubAccount}
                                        onChange={setFilterSubAccount}
                                        allowClear
                                        style={{ width: 170 }}
                                    >
                                        {subAccountOptions.map((s) => {
                                            const cc = clientColor(s);
                                            return (
                                                <Select.Option key={s} value={s}>
                                                    <Styled.FilterClientChip bg={cc.bg} fg={cc.fg}>{s}</Styled.FilterClientChip>
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </Styled.StyledSelectWrap>

                                <Styled.StyledSelectWrap>
                                    <Select
                                        showSearch
                                        placeholder="All end clients"
                                        value={filterEndClient}
                                        onChange={setFilterEndClient}
                                        allowClear
                                        style={{ width: 170 }}
                                    >
                                        {endClientOptions.map((c) => {
                                            const cc = clientColor(c);
                                            return (
                                                <Select.Option key={c} value={c}>
                                                    <Styled.FilterClientChip bg={cc.bg} fg={cc.fg}>{c}</Styled.FilterClientChip>
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </Styled.StyledSelectWrap>

                                {isCustom && (
                                    <>
                                        <Styled.StyledDatePickerWrap>
                                            <DatePicker
                                                placeholder="From date"
                                                value={fromDate ? dayjs(fromDate) : null}
                                                format="DD MMM YYYY"
                                                allowClear={false}
                                                disabledDate={disableFutureDates}
                                                onChange={(d) => { if (d) setFromDate(d.format("YYYY-MM-DD")); }}
                                                style={{ width: 170 }}
                                            />
                                        </Styled.StyledDatePickerWrap>

                                        <span className="date-sep">→</span>

                                        <Styled.StyledDatePickerWrap>
                                            <DatePicker
                                                placeholder="To date"
                                                value={toDate ? dayjs(toDate) : null}
                                                format="DD MMM YYYY"
                                                allowClear={false}
                                                disabledDate={disableFutureDates}
                                                onChange={(d) => { if (d) setToDate(d.format("YYYY-MM-DD")); }}
                                                style={{ width: 170 }}
                                            />
                                        </Styled.StyledDatePickerWrap>
                                    </>
                                )}
                            </Styled.FilterRow>

                            <Styled.DebriefCardsRow>
                                {debriefSummaryCards.map((card, idx) => (
                                    <Styled.SummaryCard key={card.label} color={card.color} light={card.light} idx={idx}>
                                        <Styled.SummaryValue color={card.color}>{card.val}</Styled.SummaryValue>
                                        <Styled.SummaryLabel>{card.label}</Styled.SummaryLabel>
                                    </Styled.SummaryCard>
                                ))}
                            </Styled.DebriefCardsRow>

                            <Styled.TableScrollArea>
                                {loading ? (
                                    <Styled.SpinWrap>
                                        <Spin size="large" />
                                    </Styled.SpinWrap>
                                ) : debriefFilteredData.length === 0 ? (
                                    <Styled.EmptyWrap>
                                        <div className="icon">📋</div>
                                        <div>No debrief data found for the selected range</div>
                                    </Styled.EmptyWrap>
                                ) : (
                                    <Styled.MainTable>
                                        <thead>
                                            <tr style={{ height: "48px" }}>
                                                <Styled.THMeta left style={{ width: "16%" }}>Recruiter Name</Styled.THMeta>
                                                <Styled.THMeta style={{ width: "7%" }}>Account<br />Name</Styled.THMeta>
                                                <Styled.THMeta style={{ width: "9%" }}><>Interviews<br />Scheduled</></Styled.THMeta>
                                                <Styled.THMeta style={{ width: "8%" }}><>Interviews<br />Done</></Styled.THMeta>
                                                <Styled.THMeta style={{ width: "8%" }}><>% Interviews<br />Done</></Styled.THMeta>
                                                <Styled.THMeta style={{ width: "9%" }}><>Debrief<br />Completed</></Styled.THMeta>
                                                <Styled.THMeta style={{ width: "8%" }}><>% Debrief<br />Completed</></Styled.THMeta>
                                                <Styled.THMeta style={{ width: "8%" }}><>Interviews<br />Not Done</></Styled.THMeta>
                                                {/* <Styled.THMeta style={{ width: "9%" }}><>Rescheduled<br />Interview</></Styled.THMeta> */}
                                                <Styled.THMeta style={{ width: "7%" }}>Client Led</Styled.THMeta>
                                                <Styled.THMeta style={{ width: "8%" }}>Candidate Led</Styled.THMeta>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {debriefRecruiterNames.map((recruiter) => {
                                                const rows = debriefGrouped[recruiter];
                                                const isOpen = !!openRecruiters[recruiter];
                                                const palette = recruiterColorMap[recruiter] || RECRUITER_PALETTE[0];

                                                const tScheduled = rows.reduce((a, r) => a + n(r.interviews_scheduled), 0);
                                                const tDone = rows.reduce((a, r) => a + n(r.interviews_done), 0);
                                                const tDebrief = rows.reduce((a, r) => a + n(r.debrief_completed), 0);
                                                const tNotDone = rows.reduce((a, r) => a + n(r.interviews_not_done), 0);
                                                const tRescheduled = rows.reduce((a, r) => a + n(r.rescheduled_interview), 0);
                                                const tClientLed = rows.reduce((a, r) => a + n(r.client_led), 0);
                                                const tCandidateLed = rows.reduce((a, r) => a + n(r.candidate_led), 0);

                                                const tInterviewPct = safePct(tDone, tScheduled);
                                                const tDebriefPct = safePct(tDebrief, tDone);

                                                return (
                                                    <React.Fragment key={recruiter}>
                                                        {/* Parent recruiter row (click to expand/collapse) */}
                                                        <Styled.RecruiterRow onClick={() => toggleRecruiter(recruiter)}>
                                                            <td className="left">
                                                                <Styled.RecruiterNameCell>
                                                                    <Styled.Chevron open={isOpen}>▶</Styled.Chevron>
                                                                    <Styled.RecruiterChip bg={palette.bg} fg={palette.fg}>
                                                                        {recruiter}
                                                                    </Styled.RecruiterChip>
                                                                </Styled.RecruiterNameCell>
                                                            </td>
                                                            <td></td>
                                                            <td><PlainNum v={tScheduled} /></td>
                                                            <td><PlainNum v={tDone} /></td>
                                                            <td><Styled.ColorPill bg="#E0E7FF" fg="#3730A3">{tInterviewPct}%</Styled.ColorPill></td>
                                                            <td><PlainNum v={tDebrief} /></td>
                                                            <td><Styled.ColorPill bg="#EDE9FE" fg="#5B21B6">{tDebriefPct}%</Styled.ColorPill></td>
                                                            <td><PlainNum v={tNotDone} /></td>
                                                            {/* <td><PlainNum v={tRescheduled} /></td> */}
                                                            <td><PlainNum v={tClientLed} /></td>
                                                            <td><PlainNum v={tCandidateLed} /></td>
                                                        </Styled.RecruiterRow>

                                                        {/* Child rows */}
                                                        {isOpen && rows.map((row, idx) => {
                                                            const cc = clientColor(row.company_name);
                                                            const interviewPct = safePct(row.interviews_done, row.interviews_scheduled);
                                                            const debriefPct = safePct(
                                                                row.debrief_completed,
                                                                row.interviews_done
                                                            );

                                                            return (
                                                                <Styled.DemandRow key={`${recruiter}-${idx}`}>
                                                                    <RoleCell title={row.role_title} />
                                                                    <td>
                                                                        <Styled.ClientChip
                                                                            style={{ background: cc.bg, color: cc.fg }}
                                                                        >
                                                                            {row.company_name || "-"}
                                                                        </Styled.ClientChip>
                                                                    </td>
                                                                    <td><PlainNum v={row.interviews_scheduled} /></td>
                                                                    <td><PlainNum v={row.interviews_done} /></td>
                                                                    <td><Styled.ColorPill bg="#E0E7FF" fg="#3730A3">{interviewPct}%</Styled.ColorPill></td>
                                                                    <td><PlainNum v={row.debrief_completed} /></td>
                                                                    <td><Styled.ColorPill bg="#EDE9FE" fg="#5B21B6">{debriefPct}%</Styled.ColorPill></td>
                                                                    <td><PlainNum v={row.interviews_not_done} /></td>
                                                                    {/* <td><PlainNum v={row.rescheduled_interview} /></td> */}
                                                                    <td><PlainNum v={row.client_led} /></td>
                                                                    <td><PlainNum v={row.candidate_led} /></td>
                                                                </Styled.DemandRow>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                );
                                            })}

                                            <Styled.GrandTotalRow>
                                                <td className="left label" colSpan="2">Grand Total</td>
                                                <td>{debriefGrand.interviews_scheduled}</td>
                                                <td>{debriefGrand.interviews_done}</td>
                                                <td>{debriefGrand.interviews_done_pct}%</td>
                                                <td>{debriefGrand.debrief_completed}</td>
                                                <td>{debriefGrand.debrief_completed_pct}%</td>
                                                <td>{debriefGrand.interviews_not_done}</td>
                                                {/* <td>{debriefGrand.rescheduled_interview}</td> */}
                                                <td>{debriefGrand.client_led}</td>
                                                <td>{debriefGrand.candidate_led}</td>
                                            </Styled.GrandTotalRow>
                                        </tbody>
                                    </Styled.MainTable>
                                )}
                            </Styled.TableScrollArea>
                        </>
                    )
                }


            </Styled.ContentWrapDiv>
        </Styled.MainDiv>
    );
};

export default PerformanceReport;